import { NgIf } from '@angular/common';
import { Component, inject, isDevMode, OnInit } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { jwtDecode } from 'jwt-decode';
import { KeycloakService } from 'keycloak-angular';
import { DecodedToken } from '@core/models/accessToken.model';
import { environment } from 'src/environments/environment';
import { Status } from '@core/models/user';
import { AuthService } from '@core/services/auth.service';
import { ProductMatrixService } from '@core/services/product-matrix.service';

import { WaveBackgroundComponent } from '@shared/components/wave-background/wave-background.component';
import { FooterComponent } from '@features/info/ui/footer/footer.component';
import { LoadingOverlayComponent } from '@shared/components/loading-overlay/loading-overlay.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [NgIf, RouterOutlet, FooterComponent, WaveBackgroundComponent, LoadingOverlayComponent],
})
export class AppComponent implements OnInit {
  readonly routerActive = inject(ActivatedRoute);
  readonly translate = inject(TranslateService);
  readonly auth = inject(AuthService);
  readonly keycloakService = inject(KeycloakService);
  readonly productMatrix = inject(ProductMatrixService);

  enterLandingPage: boolean = false;

  ngOnInit(): void {
    // institutionId is coming from One redirect to our Frontend! A User can have multiple institions and the ID is the secelected one
    const urlParams = new URLSearchParams(window.location.search);
    const institutionId = urlParams.get('institutionId') ?? undefined;
    this.initializeApp(institutionId);
  }

  private async initializeApp(institutionId?: string): Promise<void> {
    try {
      const token = await this.keycloakService.getToken();
      this.auth.setKeycloakToken(token);
      const parsedToken = this.decodeToken(token);

      const { institutions, sub: UUID, email } = parsedToken;

      // nur zum Entwickeln, damit man nicht bei jedem Refresh die Parameter neu eingeben muss
      if (environment.name !== 'prod' && !institutionId) {
        console.info(`Dev Mode - no InstitutID received. Pick from ${Object.keys(institutions)} the first one`);
        institutionId = Object.keys(institutions)[0];
      }

      if (!institutionId) {
        this.redirectToDashboard();
      }

      this.handleLogin(UUID, email, institutions, institutionId);
    } catch (error) {
      this.redirectToDashboard();
    }
  }

  private decodeToken(token: string): DecodedToken {
    return jwtDecode<DecodedToken>(token);
  }

  private handleLogin(UUID: string, email: string, institutions: any, institutionID): void {
    this.auth.login(UUID, email, institutions, institutionID).subscribe({
      next: (user) => {
        this.processUser(user);
        this.productMatrix.getBerufsGruppe().subscribe((berufsGruppe) => {
          this.auth.setBerufsGruppe(berufsGruppe);
        });
      },
      error: () => this.redirectToDashboard(),
    });
  }

  private processUser(user: any): void {
    if (user) {
      this.setupTranslation();
      this.enterLandingPage = this.isUserAllowed(user.status);
    } else {
      this.enterLandingPage = false;
    }
  }

  private setupTranslation(): void {
    this.translate.addLangs(['odfin']);
    this.translate.setDefaultLang('odfin');
  }

  private isUserAllowed(status: Status): boolean {
    if (status === Status.active || status === Status.finish) {
      return true;
    }
    this.redirectToDashboard();
    return false;
  }

  private redirectToDashboard(): void {
    window.location.href = environment.oneDashboardURL;
  }
}
